export interface SeatPos {
  x: number
  y: number
  /** vinkel i radianer, PI = vänster, 0 = höger */
  angle: number
  row: number
}

export interface HemicycleGeometry {
  seats: SeatPos[]
  seatRadius: number
  width: number
  height: number
  cx: number
  cy: number
  innerRadius: number
  outerRadius: number
}

/**
 * Placerar `total` stolar i en halvcirkel (parlamentsdiagram). Stolarna returneras
 * sorterade vänster → höger så att partierna kan fyllas i blockordning.
 */
export function hemicycleLayout(total: number, width = 760): HemicycleGeometry {
  const rows = Math.max(2, Math.min(6, Math.round(Math.sqrt(total / 3.2))))
  const cx = width / 2
  const outerRadius = width / 2 - 8
  const innerRadius = outerRadius * 0.46
  const rowGap = (outerRadius - innerRadius) / (rows - 1 || 1)

  // Stolar per rad ∝ radie.
  const radii = Array.from({ length: rows }, (_, i) => innerRadius + i * rowGap)
  const weights = radii.map((r) => r)
  const weightSum = weights.reduce((a, b) => a + b, 0)
  const perRow = weights.map((w) => Math.max(1, Math.round((w / weightSum) * total)))

  // Justera så summan stämmer exakt.
  let diff = total - perRow.reduce((a, b) => a + b, 0)
  for (let i = rows - 1; diff !== 0 && i >= 0; i = i === 0 ? rows - 1 : i - 1) {
    const step = Math.sign(diff)
    if ((perRow[i] ?? 0) + step >= 1) {
      perRow[i] = (perRow[i] ?? 0) + step
      diff -= step
    }
  }

  const seatRadius = Math.min(
    rowGap * 0.42 || 10,
    ((Math.PI * innerRadius) / (2 * ((perRow[0] ?? 1) + 1))) * 0.9,
  )

  const seats: SeatPos[] = []
  for (let row = 0; row < rows; row++) {
    const r = radii[row] ?? innerRadius
    const count = perRow[row] ?? 0
    const margin = (seatRadius * 1.3) / r // vinkelmarginal mot kanterna
    for (let i = 0; i < count; i++) {
      const frac = count === 1 ? 0.5 : i / (count - 1)
      const angle = Math.PI - margin - frac * (Math.PI - 2 * margin)
      seats.push({
        x: cx + r * Math.cos(angle),
        y: outerRadius + 8 - r * Math.sin(angle),
        angle,
        row,
      })
    }
  }

  // Vänster → höger: hög vinkel först. Tie-break på radie (inre först) ger snygga kilar.
  seats.sort((a, b) => b.angle - a.angle || a.row - b.row)

  return {
    seats,
    seatRadius,
    width,
    height: outerRadius + 16,
    cx,
    cy: outerRadius + 8,
    innerRadius,
    outerRadius,
  }
}

/**
 * Fyller stolarna med partikoder i angiven ordning (vänster → höger).
 * `order` är en lista partikoder sorterade som de ska sitta.
 */
export function assignSeats(
  seatCount: number,
  seatsByParty: Record<string, number>,
  order: string[],
): (string | null)[] {
  const out: (string | null)[] = []
  for (const code of order) {
    const n = seatsByParty[code] ?? 0
    for (let i = 0; i < n; i++) out.push(code)
  }
  while (out.length < seatCount) out.push(null)
  return out.slice(0, seatCount)
}
